import { CrmNoteType } from '@plato/data/prisma';
import { mapGroupBy } from '@plato/shared-util/map';
import { ErrorBadge } from '@plato/ui-legacy/components/ui/error-badge';
import { Container } from '@plato/ui/containers/container';
import { Separator } from '@plato/ui/primitives/separator';
import { ArrowUpRightIcon } from 'lucide-react';
import { Fragment, type ReactElement, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
    ActivityFeedModelType,
    type ActivityFeedRecord,
    type ActivityFeedVisitPayload,
} from '../../../server/types/customer-activity-feed-types.ts';
import { useActivityFeed } from '../../activity-feed/hooks/use-activity-feed.ts';
import { useLoadMoreRef } from '../hooks/use-load-more-ref.ts';
import { ActivityDay } from './activity-day.tsx';
import { ActivityFeedRecordItem } from './activity-feed-record-item.tsx';
import { FeedSection } from './feed-section.tsx';
import { GlobalActivityFeedVisitItem } from './global-activity-feed-visit-item.tsx';

const modelTypes = [
    ActivityFeedModelType.NOTE,
    ActivityFeedModelType.TASK,
    ActivityFeedModelType.INSIGHT_OUTCOME,
    ActivityFeedModelType.CUSTOMER_STATUS_CHANGE,
];

type CustomerRef = { id: string; name: string };

function isVisit(
    record: ActivityFeedRecord,
): record is ActivityFeedRecord & { payload: ActivityFeedVisitPayload } {
    return record.model_type === ActivityFeedModelType.NOTE && record.payload?.noteType === CrmNoteType.VISIT;
}

function customerOfRecord(record: ActivityFeedRecord): CustomerRef | undefined {
    return record.customer_id
        ? { id: record.customer_id, name: record.customer_name ?? record.customer_id }
        : undefined;
}

/**
 * The global feed has its own visit rows; everything else is the shared card, which also
 * skips a record that arrived without a payload.
 */
function GlobalActivityFeedRecordItem({ record }: { record: ActivityFeedRecord }): ReactElement | null {
    if (isVisit(record)) {
        // a visit's logged cards become rows of the customer box, not a glued stack
        return <GlobalActivityFeedVisitItem payload={record.payload} source="activities_page" />;
    }
    return <ActivityFeedRecordItem activity={record} source="activities_page" />;
}

/**
 * One box per customer: the name, linking through to them, over a divider and that
 * customer's cards as rows with a separator between them. The `Container` pushes the cards
 * to level 3, where they drop their own box.
 */
function CustomerGroup({ customer, children }: { customer: CustomerRef; children: ReactNode }): ReactElement {
    return (
        <Container className="flex flex-col overflow-hidden rounded-default border border-line-weak bg-container">
            <div className="border-b border-line-weak px-default py-cozy">
                {/* on the rows' padding, so the name lines up with their icons */}
                <Link
                    to={`/customers/${encodeURIComponent(customer.id)}`}
                    className="group/customer flex w-fit max-w-full items-center gap-tight px-container-cozy text-body font-medium"
                >
                    <span className="truncate group-hover/customer:underline">{customer.name}</span>
                    <ArrowUpRightIcon className="size-3.5 shrink-0 text-content-weaker group-hover/customer:text-content" />
                </Link>
            </div>
            <div className="flex flex-col px-default py-cozy">{children}</div>
        </Container>
    );
}

/** A day's records under one box per customer, in first-seen order; records without a customer come unboxed. */
function ByCustomer({ records }: { records: ActivityFeedRecord[] }): ReactElement {
    const groups = Array.from(
        mapGroupBy(records, (record) => record.customer_id),
        ([, group]) => group,
    );

    return (
        <div className="flex flex-col gap-default">
            {groups.map((group) => {
                const first = group[0];
                const customer = first && customerOfRecord(first);
                return customer ? (
                    <CustomerGroup key={customer.id} customer={customer}>
                        {group.map((record, index) => (
                            <Fragment key={record.activity_id}>
                                {index > 0 && <Separator />}
                                <GlobalActivityFeedRecordItem record={record} />
                            </Fragment>
                        ))}
                    </CustomerGroup>
                ) : (
                    <div key="no-customer" className="flex flex-col gap-cozy">
                        {group.map((record) => (
                            <GlobalActivityFeedRecordItem key={record.activity_id} record={record} />
                        ))}
                    </div>
                );
            })}
        </div>
    );
}

export type GlobalActivityFeedProps = {
    /** Pins the feed to these users ("My Activity"); otherwise the user filter applies. */
    userIds?: string[];
};

/**
 * The feed across customers: past activity, paged, grouped by day and then by customer.
 * One untitled section — upcoming tasks are left to the task list.
 */
export function GlobalActivityFeed({ userIds }: GlobalActivityFeedProps): ReactElement {
    const timeline = useActivityFeed({ userIds, modelTypes });
    const loadMoreRef = useLoadMoreRef(timeline);

    return (
        <div className="@container/feed flex flex-col gap-default">
            <FeedSection
                isLoading={timeline.isLoading || timeline.isFetchingNextPage}
                isEmpty={timeline.timeGroupedData.size === 0}
                footer={<div key={timeline.data?.pages.length} ref={loadMoreRef} />}
            >
                {Array.from(timeline.timeGroupedData, ([day, records]) => (
                    <ActivityDay key={day} date={new Date(day)}>
                        <ByCustomer records={records} />
                    </ActivityDay>
                ))}
            </FeedSection>
            {!timeline.isFetchingNextPage && timeline.error?.message && (
                <ErrorBadge>{timeline.error.message}</ErrorBadge>
            )}
        </div>
    );
}
